// src/pages/DailyReportPage.js

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate }                       from 'react-router-dom';
import api                                   from '../api';
import './DailyReportPage.css';

export default function DailyReportPage() {
  const navigate    = useNavigate();
  const previewRef  = useRef(null);

  const REPORTS = [
    { key: 'daily',   label: 'Today / Date' },
    { key: 'month',   label: 'Last 30 Days' },
    { key: 'custom',  label: 'Custom‑Range' },
    { key: 'compare', label: 'YOY / MOM' },
  ];

  const todayStr = new Date().toISOString().slice(0,10);
  const [reportType, setReportType] = useState('daily');
  const [date,    setDate]    = useState(todayStr);
  const [from,    setFrom]    = useState(todayStr);
  const [to,      setTo]      = useState(todayStr);
  const [cmpDate, setCmpDate] = useState(todayStr);

  const [daily,       setDaily]       = useState(null);
  const [monthData,   setMonthData]   = useState([]);
  const [customData,  setCustomData]  = useState([]);
  const [compareData, setCompareData] = useState([]);

  const [monthBD,  setMonthBD]  = useState({});
  const [customBD, setCustomBD] = useState({});
  const [error,    setError]    = useState('');

  // Format helpers
  const fmtDate = iso => {
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2,'0')}-${String(d.getMonth()+1).padStart(2,'0')}-${d.getFullYear()}`;
  };
  const fmtAmt = v => (Number(v)||0).toFixed(2);

  // Column helper
  const colLine = (c1, c2, c3) =>
    c1.padEnd(10) + ' ' + String(c2).padStart(5) + ' ' + String(c3).padStart(7);

  // Safe sumTotals
  const sumTotals = (arr = []) => arr.reduce((acc, r) => ({
    totalBills: acc.totalBills + (r.totalBills||0),
    totalSales: acc.totalSales + (r.totalSales||0)
  }), { totalBills:0, totalSales:0 });

  // Fetchers
  const fetchDaily = async () => {
    setError('');
    try {
      const { data } = await api.get('/bills/report/daily', { params:{ date }});
      setDaily(data);
    } catch {
      setError('Daily report failed');
    }
  };

  const fetchMonth = async () => {
    setError('');
    try {
      const res = await api.get('/bills/report/last-30-days-daily');
      setMonthData(res.data.data);
      setMonthBD(res.data.paymentBreakdown);
    } catch {
      setError('30‑day report failed');
    }
  };

  const fetchCustom = async () => {
    setError('');
    try {
      const res = await api.get('/bills/report/range', { params:{ start:from, end:to }});
      setCustomData(res.data.data);
      setCustomBD(res.data.paymentBreakdown);
    } catch {
      setError('Custom‑range report failed');
    }
  };

  const fetchCompare = async () => {
    setError('');
    try {
      const { data } = await api.get('/bills/report/comparison', { params:{ period:'month', date:cmpDate }});
      setCompareData(data);
    } catch {
      setError('YOY/MOM report failed');
    }
  };

  useEffect(() => {
    switch(reportType){
      case 'daily':   fetchDaily();   break;
      case 'month':   fetchMonth();   break;
      case 'custom':  fetchCustom();  break;
      case 'compare': fetchCompare(); break;
    }
  }, [reportType]);

  return (
    <div className="receipt-container">
      {/* Controls */}
      <div className="no-print controls">
        <button onClick={()=>navigate(-1)}>← Back</button>
        <button onClick={()=>window.print()}>Print</button>
        <div className="tabs">
          {REPORTS.map(r=>(
            <button
              key={r.key}
              className={reportType===r.key?'active':''}
              onClick={()=>setReportType(r.key)}
            >{r.label}</button>
          ))}
        </div>
        <div className="filters">
          {reportType==='daily' && <label>Date:<input type="date" value={date} onChange={e=>setDate(e.target.value)}/></label>}
          {['month','custom'].includes(reportType) && <>
            <label>From:<input type="date" value={from} onChange={e=>setFrom(e.target.value)}/></label>
            <label>To:<input   type="date" value={to}   onChange={e=>setTo(e.target.value)}/></label>
          </>}
          {reportType==='compare' && <label>Ref:<input type="date" value={cmpDate} onChange={e=>setCmpDate(e.target.value)}/></label>}
          <button onClick={()=>{
            switch(reportType){
              case 'daily':   fetchDaily();   break;
              case 'month':   fetchMonth();   break;
              case 'custom':  fetchCustom();  break;
              case 'compare': fetchCompare(); break;
            }
          }}>Refresh</button>
        </div>
        {error && <div className="error-msg">{error}</div>}
      </div>

      {/* Header */}
      <div className="header">
        <div className="center bold">Sales Report</div>
        <div className="center">Date: {fmtDate(new Date().toISOString())}</div>
        <div className="separator" />
      </div>

      {/* Body */}
      <div className="body" ref={previewRef}>
        {/* DAILY */}
        {reportType==='daily' && daily && (
          <>
            <div className="summary-line total">
              {colLine('Bills', daily.totalBills, `₹${fmtAmt(daily.totalSales)}`)}
            </div>
            <div className="summary-line">
              {colLine('Tax', '', `₹${fmtAmt(daily.totalTax)}`)}
            </div>
            <div className="bold">Payment Breakdown</div>
            {(daily.paymentBreakdown ? Object.entries(daily.paymentBreakdown) : []).map(([mode,amt])=>(
              <div key={mode} className="summary-line">
                {colLine(mode, '', `₹${fmtAmt(amt)}`)}
              </div>
            ))}
          </>
        )}

        {/* MONTH & CUSTOM */}
        {(reportType==='month' || reportType==='custom') && (
          <>
            <pre className="preformatted">
              {colLine('Date','Bills','Sales')}
              {((reportType==='month'?monthData:customData)||[]).map(r=>'\n'+colLine(fmtDate(r.billDate), r.totalBills, `₹${fmtAmt(r.totalSales)}`))}
            </pre>
            <div className="separator" />
            <div className="summary-line total">
              {colLine('Total',
                sumTotals(reportType==='month'?monthData:customData).totalBills,
                `₹${fmtAmt(sumTotals(reportType==='month'?monthData:customData).totalSales)}`
              )}
            </div>
            <div className="bold">Payment Breakdown</div>
            {Object.entries(reportType==='month'?monthBD:customBD||{}).map(([mode,amt])=>(
              <div key={mode} className="summary-line">
                {colLine(mode, '', `₹${fmtAmt(amt)}`)}
              </div>
            ))}
          </>
        )}

        {/* YOY/MOM */}
        {reportType==='compare' && (
          <pre className="preformatted">
            {colLine('Period','', 'Sales')}
            {((compareData)||[]).map(r=>'\n'+colLine(r.month||r.year, '', `₹${fmtAmt(r.sales)}`))}
          </pre>
        )}
      </div>
    </div>
  );
}
